import React from 'react';
import {twMerge} from 'tailwind-merge';

import {usePanelConfigContext} from '../../../PanelRunsLinePlot/PanelConfigContext';
import {LinePlotExcludeOutliers} from './LinePlotExcludeOutliers';
import {LinePlotGroupingSetting} from './LinePlotGroupingSetting';
import {LinePlotMaxRuns} from './LinePlotMaxRuns';
import {LinePlotPointAggregation} from './LinePlotPointAggregation';
import {useLinePlotSettingsContext} from './LinePlotSettingsContextProvider';
import {LinePlotSmoothing} from './LinePlotSmoothing';
import {LinePlotXAxis} from './LinePlotXAxis';
import {LinePlotYAxis} from './LinePlotYAxis';
import {DEFAULT_LINE_PLOT_NUM_RUNS_NEW} from './maxRunDefaults';
import {throttleMaxRunsToLimit} from './utils/getCascadingMaxRuns';
import {useLinePlotSettingsWithDefaults} from './utils/getLinePlotSettings';

export const LinePlotDataTab = () => {
  const {maxRunsLimit} = usePanelConfigContext();
  const {
    clearSetting,
    displayLinePlotSettings,
    linePlotSettings,
    settingLevel,
    settingOverrideCounts,
    trackLinePlotSetting,
    updateLinePlotSettings,
  } = useLinePlotSettingsContext();

  const {
    excludeOutliers,
    limit,
    pointVisualizationMethod,
    smoothingType,
    smoothingWeight,
    xAxis,
    xAxisMax,
    xAxisMin,
    xLogScale,
    xAxisFormat,
    yAxisMax,
    yAxisMin,
    yLogScale,
  } = useLinePlotSettingsWithDefaults(displayLinePlotSettings);

  return (
    <div
      data-test="workspace-line-plot-data-settings"
      className={twMerge('flex flex-col', 'gap-20 py-16', 'h-full w-full')}>
      <LinePlotXAxis
        clearSetting={clearSetting}
        displayValues={{
          xAxis,
          xAxisMax,
          xAxisMin,
          xLogScale,
          xAxisFormat,
        }}
        numOverrides={settingOverrideCounts}
        settingLevel={settingLevel}
        trackSetting={trackLinePlotSetting}
        updateSetting={updateLinePlotSettings}
        values={{
          xAxis: linePlotSettings?.xAxis,
          xAxisMax: linePlotSettings?.xAxisMax,
          xAxisMin: linePlotSettings?.xAxisMin,
          xLogScale: linePlotSettings?.xLogScale,
          xAxisFormat: linePlotSettings?.xAxisFormat,
        }}
      />
      <LinePlotYAxis
        clearSetting={clearSetting}
        displayValues={{
          yAxisMax,
          yAxisMin,
          yLogScale,
        }}
        numOverrides={settingOverrideCounts}
        settingLevel={settingLevel}
        trackSetting={trackLinePlotSetting}
        updateSetting={updateLinePlotSettings}
        values={{
          yAxisMax: linePlotSettings?.yAxisMax,
          yAxisMin: linePlotSettings?.yAxisMin,
          yLogScale: linePlotSettings?.yLogScale,
        }}
      />
      <LinePlotSmoothing
        clearSetting={clearSetting}
        displayValues={{
          smoothingType,
          smoothingWeight,
        }}
        numOverrides={settingOverrideCounts}
        settingLevel={settingLevel}
        trackSetting={trackLinePlotSetting}
        updateSetting={updateLinePlotSettings}
        values={{
          smoothingType: linePlotSettings?.smoothingType,
          smoothingWeight: linePlotSettings?.smoothingWeight,
        }}
      />
      <LinePlotExcludeOutliers
        clearSetting={clearSetting}
        displayValues={{
          excludeOutliers,
        }}
        numOverrides={settingOverrideCounts}
        settingLevel={settingLevel}
        updateSetting={updateLinePlotSettings}
        trackSetting={trackLinePlotSetting}
        values={{
          excludeOutliers: linePlotSettings?.excludeOutliers,
        }}
      />
      <LinePlotPointAggregation
        clearSetting={clearSetting}
        displayValues={{
          pointVisualizationMethod,
        }}
        numOverrides={settingOverrideCounts}
        settingLevel={settingLevel}
        onPointVisualizationMethodChange={value => {
          updateLinePlotSettings({
            pointVisualizationMethod: value,
          });
          trackLinePlotSetting('pointVisualizationMethod', value.toString());
        }}
        values={{
          pointVisualizationMethod: linePlotSettings?.pointVisualizationMethod,
        }}
      />

      <LinePlotMaxRuns
        clearSetting={clearSetting}
        displayValues={{
          maxRuns:
            throttleMaxRunsToLimit(limit, maxRunsLimit) ??
            DEFAULT_LINE_PLOT_NUM_RUNS_NEW,
          pointVisualizationMethod,
        }}
        numOverrides={settingOverrideCounts}
        settingLevel={settingLevel}
        trackSetting={trackLinePlotSetting}
        updateSetting={updateLinePlotSettings}
        values={{
          maxRuns: linePlotSettings?.limit ?? linePlotSettings?.maxRuns,
        }}
      />
      <LinePlotGroupingSetting />
    </div>
  );
};
